/**
 * "Leitores parecidos" — cache POR APARELHO, 1 dia BR (blueprint
 * `docs/systems/people-suggestions.md` §2 e §4.4).
 *
 * ## Por que existe
 *
 * A Home abre em toda sessão e cada chamada da callable custa ~31 reads. O
 * aparelho guarda a resposta pelo dia BR (`dayKeyForBR`, nunca UTC: às 22h BR
 * o UTC já virou o dia e a lista "venceria" duas horas antes da meia-noite de
 * quem usa). `ok`/`empty`/`opted_out` valem o dia; `pending` (o cron ainda não
 * rodou) revalida em 6 h. **Erro não entra no cache**: falha não pode ficar
 * guardada, a tela só não mostra o bloco nesta sessão.
 *
 * ## As travas que o cache NÃO pode enfraquecer (§2)
 *
 *  1. chave por `uid` (`livra_people_suggestions_v1_{uid}`) e o `uid` gravado
 *     dentro do valor: troca de conta no mesmo aparelho nunca reaproveita;
 *  2. descartado no logout (`clearAllPeopleSuggestionsCaches`, chamado pelo
 *     `signOutEverywhere`) e quando o dono mexe na chave do opt-out;
 *  3. `hiddenLocal`: quem caiu num Seguir recusado (`permission-denied`) some
 *     do cartão pelo resto do dia, sem nova chamada — e sem texto de bloqueio.
 *
 * ## Uma loja só, assinável
 *
 * A Home decide a PRESENÇA do bloco antes do render (`homeSequence`) e o
 * carrossel é quem busca. Os dois leem esta loja: quando a resposta chega, a
 * Home re-renderiza junto (`useSyncExternalStore`). `localStorage` sempre em
 * try/catch; sem ele (aba privada, armazenamento bloqueado) a loja segue em
 * memória durante a sessão.
 */
import { dayKeyForBR } from "../../../lib/time";
import type { PeopleSuggestionsResponse } from "../../../services/firebase/peopleSuggestionsService";
import { PEOPLE_LIST_MIN } from "./peopleSuggestions";

export const PEOPLE_CACHE_PREFIX = "livra_people_suggestions_v1_";

/** `pending` = o cron ainda não gerou a lista: pergunta de novo depois disto. */
export const PEOPLE_PENDING_REVALIDATE_MS = 6 * 60 * 60 * 1000;

/** Depois de um erro, a seção fica fora da tela por este tempo (em memória). */
export const PEOPLE_ERROR_BACKOFF_MS = 10 * 60 * 1000;

export interface PeopleCacheEntry {
  uid: string;
  /** Dia BR em que a resposta chegou. */
  dayKey: string;
  fetchedAt: number;
  response: PeopleSuggestionsResponse;
  /** Seguidos NESTE aparelho: viram "✓ Seguindo" sem nova chamada. */
  followedLocal: string[];
  /** Seguir recusado (`permission-denied`): o cartão some, em silêncio. */
  hiddenLocal: string[];
}

export type PeoplePresence = "present" | "absent" | "unknown";

const memory = new Map<string, PeopleCacheEntry>();
const errors = new Map<string, number>();
const listeners = new Set<() => void>();
let version = 0;

function emit(): void {
  version += 1;
  listeners.forEach((l) => l());
}

export function subscribePeopleCache(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Muda a cada escrita — o snapshot do `useSyncExternalStore`. */
export function peopleCacheVersion(): number {
  return version;
}

function keyFor(uid: string): string {
  return `${PEOPLE_CACHE_PREFIX}${uid}`;
}

function isEntry(v: unknown, uid: string): v is PeopleCacheEntry {
  if (!v || typeof v !== "object") return false;
  const e = v as Partial<PeopleCacheEntry>;
  const r = e.response as Partial<PeopleSuggestionsResponse> | undefined;
  return (
    e.uid === uid &&
    typeof e.dayKey === "string" &&
    typeof e.fetchedAt === "number" &&
    !!r &&
    typeof r.status === "string" &&
    Array.isArray(r.people) &&
    Array.isArray(e.followedLocal) &&
    Array.isArray(e.hiddenLocal)
  );
}

function readRaw(uid: string): PeopleCacheEntry | null {
  try {
    const raw = localStorage.getItem(keyFor(uid));
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (isEntry(parsed, uid)) return parsed;
    }
  } catch {
    /* storage indisponível ou JSON corrompido: cai na memória */
  }
  return memory.get(uid) ?? null;
}

function writeRaw(entry: PeopleCacheEntry): void {
  memory.set(entry.uid, entry);
  try {
    localStorage.setItem(keyFor(entry.uid), JSON.stringify(entry));
  } catch {
    /* sem storage: a memória segura a sessão */
  }
}

/** A entrada de HOJE (dia BR) deste `uid`, ou `null`. */
export function readPeopleCache(uid: string | null | undefined, now: Date = new Date()): PeopleCacheEntry | null {
  if (!uid) return null;
  const entry = readRaw(uid);
  if (!entry) return null;
  return entry.dayKey === dayKeyForBR(now) ? entry : null;
}

let snap: { uid: string | null; version: number; dayKey: string; entry: PeopleCacheEntry | null } | null = null;

/**
 * Snapshot ESTÁVEL para `useSyncExternalStore`: o mesmo objeto enquanto nada
 * foi escrito (e o dia BR não virou). Ler o storage a cada render devolveria
 * um objeto novo por chamada e o React entraria em laço.
 */
export function getPeopleCacheSnapshot(uid: string | null | undefined): PeopleCacheEntry | null {
  const key = uid ?? null;
  const dayKey = dayKeyForBR();
  if (snap && snap.uid === key && snap.version === version && snap.dayKey === dayKey) return snap.entry;
  const entry = readPeopleCache(key);
  snap = { uid: key, version, dayKey, entry };
  return entry;
}

/** Grava a resposta da callable. Só respostas de SUCESSO chegam aqui. */
export function writePeopleCache(
  uid: string,
  response: PeopleSuggestionsResponse,
  now: Date = new Date(),
): PeopleCacheEntry {
  const prev = readPeopleCache(uid, now);
  const entry: PeopleCacheEntry = {
    uid,
    dayKey: dayKeyForBR(now),
    fetchedAt: now.getTime(),
    response,
    // Revalidação do mesmo dia (pending → ok) não esquece o que já se fez aqui.
    followedLocal: prev?.followedLocal ?? [],
    hiddenLocal: prev?.hiddenLocal ?? [],
  };
  errors.delete(uid);
  writeRaw(entry);
  emit();
  return entry;
}

function patchLocal(uid: string, field: "followedLocal" | "hiddenLocal", personUid: string): void {
  const entry = readPeopleCache(uid);
  if (!entry) return;
  if (entry[field].includes(personUid)) return;
  writeRaw({ ...entry, [field]: [...entry[field], personUid] });
  emit();
}

export function markPeopleFollowedLocal(uid: string, personUid: string): void {
  patchLocal(uid, "followedLocal", personUid);
}

export function markPeopleHiddenLocal(uid: string, personUid: string): void {
  patchLocal(uid, "hiddenLocal", personUid);
}

/** Descarta a lista deste dono (opt-out mexido, troca de estado). */
export function clearPeopleCache(uid: string | null | undefined): void {
  if (!uid) return;
  memory.delete(uid);
  errors.delete(uid);
  try {
    localStorage.removeItem(keyFor(uid));
  } catch {
    /* nada a fazer */
  }
  emit();
}

/** Logout: some com a lista de TODA conta que passou por este aparelho. */
export function clearAllPeopleSuggestionsCaches(): void {
  memory.clear();
  errors.clear();
  hiddenMemory.clear();
  dismissedMemory.clear();
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (
        k &&
        (k.startsWith(PEOPLE_CACHE_PREFIX) || k.startsWith(PEOPLE_HIDDEN_PREFIX) || k.startsWith(PEOPLE_DISMISSED_PREFIX))
      ) {
        doomed.push(k);
      }
    }
    doomed.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* nada a fazer */
  }
  emit();
}

// ── "Esconder até segunda" (× do cabeçalho, mock leitores-parecidos-v2 E7) ──
//
// Por APARELHO (E7b): esconder uma semana é gesto leve, e reaparecer em outro
// aparelho não fere ninguém — "nunca mais" é a chave de Ajustes, que é da conta.
// Guarda a semana da lista escondida e a segunda seguinte. Até essa segunda a
// seção some SEM chamar a callable; dali em diante só volta quando a lista
// for outra (o cron de segunda pode rodar depois da meia-noite).

export const PEOPLE_HIDDEN_PREFIX = "livra_people_hidden_v1_";

export interface PeopleHiddenMark {
  /** `weekStartDayKey` da lista que estava na tela. */
  week: string;
  /** Dia BR da segunda seguinte (`week` + 7). */
  until: string;
}

const hiddenMemory = new Map<string, PeopleHiddenMark>();

/** `YYYY-MM-DD` + n dias, em calendário puro (sem fuso: é só aritmética de data). */
export function addDaysToDayKey(dayKey: string, n: number): string {
  const [y, m, d] = dayKey.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return t.toISOString().slice(0, 10);
}

function readHidden(uid: string): PeopleHiddenMark | null {
  try {
    const raw = localStorage.getItem(`${PEOPLE_HIDDEN_PREFIX}${uid}`);
    if (raw) {
      const v = JSON.parse(raw) as Partial<PeopleHiddenMark>;
      if (typeof v.week === "string" && typeof v.until === "string") return { week: v.week, until: v.until };
    }
  } catch {
    /* storage indisponível: cai na memória */
  }
  return hiddenMemory.get(uid) ?? null;
}

export function hidePeopleUntilNextList(uid: string, weekStartDayKey: string): void {
  const mark: PeopleHiddenMark = { week: weekStartDayKey, until: addDaysToDayKey(weekStartDayKey, 7) };
  hiddenMemory.set(uid, mark);
  try {
    localStorage.setItem(`${PEOPLE_HIDDEN_PREFIX}${uid}`, JSON.stringify(mark));
  } catch {
    /* sem storage: vale a sessão */
  }
  emit();
}

/** O "Desfazer" do aviso. */
export function unhidePeople(uid: string): void {
  hiddenMemory.delete(uid);
  try {
    localStorage.removeItem(`${PEOPLE_HIDDEN_PREFIX}${uid}`);
  } catch {
    /* nada a fazer */
  }
  emit();
}

/**
 * Escondida agora? Antes da segunda: sim, sem olhar a lista. Da segunda em
 * diante: só enquanto a lista de hoje for a MESMA semana que foi escondida.
 */
export function isPeopleHidden(uid: string, now: Date = new Date()): boolean {
  const mark = readHidden(uid);
  if (!mark) return false;
  if (dayKeyForBR(now) < mark.until) return true;
  const entry = readPeopleCache(uid, now);
  return !!entry && entry.response.weekStartDayKey === mark.week;
}

// ── "Não mostrar esta pessoa" (× do cartão, 15/10, §6 reaberto) ─────────────
//
// Por APARELHO e por LISTA, como o × do cabeçalho (decisão do fundador 15/10:
// "só até segunda"). Guarda os uids dispensados junto da `weekStartDayKey` da
// lista em que foram dispensados; a lista nova de segunda tem outra semana, e
// a marca deixa de valer sozinha (a pessoa pode voltar, se o cron a escolher
// de novo). Fica fora da entrada do dia (`hiddenLocal`) de propósito: aquela
// some à meia-noite, e o "até segunda" tem de atravessar os dias da semana.

export const PEOPLE_DISMISSED_PREFIX = "livra_people_dismissed_v1_";

/** Teto da marca: a lista entrega no máximo 5; folga para reservas que entram no lugar. */
export const PEOPLE_DISMISSED_MAX = 30;

export interface PeopleDismissedMark {
  /** `weekStartDayKey` da lista em que as pessoas foram dispensadas. */
  week: string;
  uids: string[];
}

const dismissedMemory = new Map<string, PeopleDismissedMark>();

function readDismissed(uid: string): PeopleDismissedMark | null {
  try {
    const raw = localStorage.getItem(`${PEOPLE_DISMISSED_PREFIX}${uid}`);
    if (raw) {
      const v = JSON.parse(raw) as Partial<PeopleDismissedMark>;
      if (typeof v.week === "string" && Array.isArray(v.uids)) {
        return { week: v.week, uids: v.uids.filter((x): x is string => typeof x === "string") };
      }
    }
  } catch {
    /* storage indisponível ou JSON corrompido: cai na memória */
  }
  return dismissedMemory.get(uid) ?? null;
}

function writeDismissed(uid: string, mark: PeopleDismissedMark): void {
  dismissedMemory.set(uid, mark);
  try {
    localStorage.setItem(`${PEOPLE_DISMISSED_PREFIX}${uid}`, JSON.stringify(mark));
  } catch {
    /* sem storage: vale a sessão */
  }
  emit();
}

/** Uids dispensados NESTA lista. Marca de outra semana = nada dispensado. */
export function dismissedPeopleFor(uid: string | null | undefined, week: string): Set<string> {
  if (!uid || !week) return new Set();
  const mark = readDismissed(uid);
  return mark && mark.week === week ? new Set(mark.uids) : new Set();
}

export function dismissPersonForWeek(uid: string, week: string, personUid: string): void {
  if (!week) return;
  const prev = readDismissed(uid);
  // Marca de semana velha é descartada inteira: a lista nova recomeça limpa.
  const uids = prev && prev.week === week ? prev.uids.filter((x) => x !== personUid) : [];
  writeDismissed(uid, { week, uids: [...uids, personUid].slice(-PEOPLE_DISMISSED_MAX) });
}

/** O "Desfazer" do aviso do cartão. */
export function undismissPerson(uid: string, week: string, personUid: string): void {
  const prev = readDismissed(uid);
  if (!prev || prev.week !== week || !prev.uids.includes(personUid)) return;
  writeDismissed(uid, { week, uids: prev.uids.filter((x) => x !== personUid) });
}

/** A semana de uma entrada, como o client a usa (a do servidor; senão o dia). */
export function peopleEntryWeek(entry: PeopleCacheEntry | null | undefined): string {
  return entry?.response.weekStartDayKey || entry?.dayKey || "";
}

/** Erro de rede/servidor: NÃO vai para o cache; segura a seção fora por um tempo. */
export function markPeopleFetchError(uid: string, now: number = Date.now()): void {
  errors.set(uid, now);
  emit();
}

export function hasRecentPeopleError(uid: string, now: number = Date.now()): boolean {
  const at = errors.get(uid);
  return at !== undefined && now - at < PEOPLE_ERROR_BACKOFF_MS;
}

/** A entrada precisa (de novo) da callable? */
export function peopleCacheNeedsFetch(entry: PeopleCacheEntry | null, now: number = Date.now()): boolean {
  if (!entry) return true;
  if (entry.response.status !== "pending") return false;
  return now - entry.fetchedAt >= PEOPLE_PENDING_REVALIDATE_MS;
}

/**
 * Presença do bloco ANTES do render (contrato do `homeSequence`), sem chamar
 * a callable (§4.4):
 *  - `absent`: sem conta real, opt-out do dono, flag desligada, erro recente,
 *    escondida pelo × até a lista nova (`isPeopleHidden`, só com `respectHidden`),
 *    o cache de hoje diz `empty`/`opted_out`/`pending` fresco/< 3 pessoas,
 *    ou todas as pessoas da lista foram dispensadas pelo × do cartão;
 *  - `present`: cache de hoje `ok` com ≥ 3;
 *  - `unknown`: sem cache de hoje (ou `pending` vencido) — a Home reserva um
 *    esqueleto de altura fixa e a chamada sai quando ele chega perto da tela.
 */
export function derivePeoplePresence({
  uid,
  isAnonymous,
  enabledForOwner,
  flagEnabled,
  respectHidden = false,
  now = Date.now(),
}: {
  uid: string | null | undefined;
  isAnonymous: boolean;
  enabledForOwner: boolean;
  flagEnabled: boolean;
  /** Portas da Home: o × as esconde. A aba Privadas ignora (lá a lista é o assunto). */
  respectHidden?: boolean;
  now?: number;
}): PeoplePresence {
  if (!uid || isAnonymous || !enabledForOwner || !flagEnabled) return "absent";
  if (hasRecentPeopleError(uid, now)) return "absent";
  if (respectHidden && isPeopleHidden(uid, new Date(now))) return "absent";
  const entry = readPeopleCache(uid, new Date(now));
  if (!entry) return "unknown";
  const { status, people } = entry.response;
  if (status === "pending") return peopleCacheNeedsFetch(entry, now) ? "unknown" : "absent";
  if (status !== "ok") return "absent";
  if (people.length < PEOPLE_LIST_MIN) return "absent";
  // Dispensou TODO mundo pelo × do cartão: não sobra nada a mostrar até a lista
  // nova. Dispensar alguns NÃO derruba a seção (o corte de 3 é da lista do servidor).
  const dismissed = dismissedPeopleFor(uid, peopleEntryWeek(entry));
  return people.some((p) => !dismissed.has(p.uid)) ? "present" : "absent";
}
